import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  Accordion,
  AccordionDetails,
  AccordionStack,
  AccordionSummary,
} from 'base-ui'

import { useGlobalConfigContext } from '../../../../global-config'
import { productAccessorGetCautions } from '../../../model/productAccessor/productAccessorGetCautions'
import { productAccessorGetSubstances } from '../../../model/productAccessor/productAccessorGetSubstances'
import { useProductPageContext } from '../../productPageContext'
import { ProductDescriptionCautions } from '../ProductDescriptionCautions'
import { ProductDescriptionDocumentLinks } from '../ProductDescriptionDocumentLinks'
import { ProductDescriptionIngredientsSection } from '../ProductDescriptionIngredients/ProductDescriptionIngredientsSection'
import { ProductDescriptionParsedField } from '../ProductDescriptionParsedField'
import { useProductDescriptionSections } from '../useProductDescriptionSections'

export const ProductDescriptionWithMultipleAccordions: FC = () => {
  const { product } = useProductPageContext()
  const { tenant } = useGlobalConfigContext()

  const { additionalInformation, descriptionLongIfap, hasIfapSections } =
    useProductDescriptionSections()

  const cautions = productAccessorGetCautions(product, tenant)
  const { activeSubstances, additiveSubstances } =
    productAccessorGetSubstances(product)

  // if there is ifap content we prioritze the ifap instead of additionalInformation (product split attributes)
  const sectionsToRender = hasIfapSections
    ? descriptionLongIfap
    : additionalInformation

  return (
    <>
      {activeSubstances.length > 0 && (
        <ProductDescriptionIngredientsSection
          header={
            <FormattedMessage id="productAdditonalInfos.headline.activeSubstances" />
          }
          substances={activeSubstances}
        />
      )}

      {additiveSubstances.length > 0 && (
        <ProductDescriptionIngredientsSection
          header={
            <FormattedMessage id="productAdditonalInfos.headline.additiveSubstances" />
          }
          substances={additiveSubstances}
        />
      )}

      {sectionsToRender && sectionsToRender.length > 0 ? (
        <AccordionStack>
          {sectionsToRender.map((section, index) => {
            /**
             * if there are pictograms, expand the sections containing them by default, otherwise expand the first section
             */
            const showCautions = Boolean(
              section.showCautions && cautions.length > 0,
            )
            const expanded: boolean = cautions.length === 0 && index === 0

            return (
              <Accordion
                defaultExpanded={expanded || showCautions}
                key={section.content}
              >
                {section.header ? (
                  <AccordionSummary>{section.header}</AccordionSummary>
                ) : null}
                <AccordionDetails>
                  {section.showCautions ? <ProductDescriptionCautions /> : null}
                  <ProductDescriptionParsedField
                    className="last:mb-0"
                    field={section.content}
                  />
                </AccordionDetails>
              </Accordion>
            )
          })}
        </AccordionStack>
      ) : null}

      <ProductDescriptionDocumentLinks />
    </>
  )
}
